import { Btn, IconBtn, Bx, Hamburger } from '@curry-group/mui-curcuma';
import { HeaderHeadbarContainer } from '../headbar-container';
import { faPlus, faSearch, faSlidersV, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useTheme from '@material-ui/core/styles/useTheme';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';
import {
  changeQueryTermAction,
  changeQueryTypeAction,
  changeQueryCategoriesAction,
  changeQuerySortAction,
  changeQuerySearchTypeAction,
  hideFilterAction,
  resetSearchObjectAction,
  showFilterAction,
  fetchFilterRequestAction,
  setSelectedSearchAction,
  changeQueryDestinationAction,
} from '../../../data/actions/filter';
import {
  setBotSearchAction,
  botResponseEndAction,
  fetchBotExpertAnswerRequestAction
} from '../../../data/actions/bot';
import { clearSearchEntriesAction, fetchFirstPageSearchEntriesRequestAction, setSearchWorkingAction } from '../../../data/actions/search';
import { useFilterState } from '../../../data/sagas/filter';
import { flowSteps, getReferrer, MTOLogo } from '../../../helper';
import { ISearchRequestBody } from '../../../model/search/SearchRequest';
import { HeaderFilter } from '../../filter';
import { debounce as _debounce } from 'lodash';
import { Collapse, Grid } from '@material-ui/core';
import { INavbarOpenProps } from '../../navbar';
import { setNavbarOpenAction } from '../../../data/actions/foundation';
import { useIsMobile } from '../../../helper/hooks/isMobile';
import { HeadBar } from '../../head-bar';
import { HeadSearchInput } from '../../head-search-input';
import { MobileHeadBar } from '../../mobile-head-bar';
import { NavBarLogo } from '../../navbar-logo';
import { fetchFirstPageNewsRequestAction, resetNewsReferences, setNewsAllConcepts, setNewsWorking } from '../../../data/actions/news';
import { faArrowUp, faMessageBot } from '@fortawesome/pro-solid-svg-icons';
import { resetFlowAction, setFlowAnonymousAction, setFlowTypeAction } from '../../../data/actions/flow';

export interface IDashboardHeaderProps {
  routeToSearch: boolean;
  alias: string;
  filterItem: any;
  filterAlias: string;
  filterVisible: boolean;
  filterWorking: boolean;
  filterSwitch?: boolean;
  placeholder?: string;
  searchResult?: any;
  searchObject?: ISearchRequestBody;
  searchTypes?: Array<string>;
  selectedSearch?: string;
  changeQueryTerm?: (term: string, noFilterAction?: boolean) => void;
  changeQueryTypeAction?: (thingType: string) => void;
  changeQueryDestinationAction?: (destinationAlias: string) => void;
  changeQueryCategoriesAction?: (category: any, noToggle: boolean, parentCategoryId: string) => void;
  changeQuerySortAction: (sortId: string) => void;
  changeQuerySearchTypeAction?: (searchTypeId: string) => void;
  resetSearchObject?: () => void;
  setSelectedSearch?: (searchAlias: string, keepQuery?: boolean) => void;
  showFilterAction?: () => void;
  hideFilterAction?: () => void;
  fetchFirstPageSearchEntries?: () => void;
  debouncedFetchBotExpertAnswer?: () => void;
  debouncedFetchFirstPageSearchEntries?: (defaultResultsOnly?: boolean) => void;
  debouncedBotFetchFirstPageSearchEntriesAction?: () => void;
  setSearchWorkingAction?: (working: boolean) => void;
  canCreateContent?: boolean;
  createContentCaption?: string;
  onCreateContentClicked?: () => void;
  memoryList?: boolean;
  memoryIds?: Array<string>;
  setNewsAllConcepts?: () => void;
  newsAllConcepts?: boolean;
  botSearchActive?: boolean;
  setBotSearchAction?: () => void;
  setBotResponseAction?: (botAnswer: string, botRequestId: string) => void,
  botAnswer?: string;
  botSearchInitial?: boolean;
}

export const DashboardHeader: React.FC<IDashboardHeaderProps> = ({
  alias,
  filterItem,
  filterAlias,
  routeToSearch,
  filterVisible,
  filterWorking,
  filterSwitch,
  placeholder,
  searchObject,
  searchTypes,
  selectedSearch,
  searchResult,
  changeQueryTerm,
  changeQueryTypeAction,
  changeQueryDestinationAction,
  changeQueryCategoriesAction,
  changeQuerySortAction,
  changeQuerySearchTypeAction,
  showFilterAction,
  hideFilterAction,
  fetchFirstPageSearchEntries,
  debouncedFetchBotExpertAnswer,
  debouncedFetchFirstPageSearchEntries,
  debouncedBotFetchFirstPageSearchEntriesAction,
  setSearchWorkingAction,
  resetSearchObject,
  setSelectedSearch,
  canCreateContent,
  createContentCaption,
  onCreateContentClicked,
  memoryList,
  memoryIds,
  setNewsAllConcepts,
  newsAllConcepts,
  botSearchActive,
  setBotSearchAction,
  setBotResponseAction,
  botAnswer,
  botSearchInitial
}) => {
  const theme: any = useTheme();
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const history = useHistory();
  const pathSplit = history.location.pathname.split('/');
  const onSearchRoute = pathSplit[pathSplit.length - 1] === 'search';
  const onDashboardRoute = pathSplit[pathSplit.length - 1] === 'dashboard';
  const onStartRoute = history.location.pathname === '/start';

  const initialSearchRef = useRef(onSearchRoute);
  const initiallyEmpty = useRef(false);

  useEffect(() => {
    if ((!memoryIds || memoryIds?.length === 0) && !initiallyEmpty.current) {
      initiallyEmpty.current = true;
    }
  });

  useEffect(() => {
    // Guard against firing the initial search twice (the header remounts when the mobile/desktop
    // breakpoint resolves, resetting initialSearchRef): skip if a search is already in flight or
    // has already produced results. This stops the duplicate concurrent search on landing.
    if (initialSearchRef.current && searchObject && filterItem && !searchResult) {
      initialSearchRef.current = false;
      fetchFirstPageSearchEntries?.();
    }
  }, [initialSearchRef, fetchFirstPageSearchEntries, searchObject, filterItem, searchResult]);

  useEffect(() => {
    if (botSearchActive && !searchResult) {
      debouncedFetchFirstPageSearchEntries?.(true);
    }
  }, [botSearchActive, searchResult, debouncedFetchFirstPageSearchEntries]);

  const searchInactive = initiallyEmpty.current && !!memoryList && (!memoryIds || memoryIds?.length === 0);
  const botSearchActiveLocal = !!filterItem?.content?.botSearchActive;

  const dispatcher = {
    setQnaFlowInitAction: () => {
      dispatch(setFlowTypeAction({type: 'qna'}));
      dispatch(setFlowAnonymousAction({anonymous: false}));
    },
    resetFlowAction: () => dispatch(resetFlowAction?.({})),
    resetNewsReferencesAction: () => dispatch(resetNewsReferences?.()),
  };
  return (
    <>
      <HeadBar
        transparent={false}
        backButtonLink={
          onSearchRoute || onDashboardRoute
            ? () => {
                const referrer = getReferrer(history.location.search);
                if (referrer) {
                  if (alias === 'news' || alias === 'magazine' || alias === 'forum' || alias === 'groups-projects' || alias === 'experts-actors') {
                    changeQueryTerm?.('');
                    if (botSearchActive) {
                      setBotResponseAction?.('', '');
                    }
                    resetSearchObject?.();
                    
                    setSearchWorkingAction?.(true);
                    debouncedFetchFirstPageSearchEntries?.();
                    history.push(referrer);
                  } else {
                    history.push(referrer);
                  }
                } else {
                  resetSearchObject?.();
                  history.push(match.url);
                }
              }
            : undefined
        }
      >
        <HeaderHeadbarContainer>
        {!searchInactive && <Bx flexGrow={1} flexShrink={1} flexBasis="auto" display={'flex'}>
            {!onStartRoute && canCreateContent && (flowSteps[alias] || onCreateContentClicked) && (
              <Bx mr={1}>
                <IconBtn
                  size={'medium'}
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    if (alias === 'forum') {
                      dispatcher.resetFlowAction?.();
                      dispatcher.setQnaFlowInitAction?.();
                    }
                    onCreateContentClicked ? onCreateContentClicked() : history.push(match.url + '/flow');
                  }}
                  title={createContentCaption || 'Neuer Beitrag'}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </IconBtn>
              </Bx>
            )}
            <HeadSearchInput
              searchIcon={null}
              // searchIcon={!!botSearchActive && <FontAwesomeIcon icon={faMessageBot} />}
              input={{
                id: 'head-search',
                value: searchObject?.queryTerm,
                placeholder: alias === 'start'
                  ? filterItem?.content?.title || 'Medtec Online durchsuchen'
                  : !!botSearchActive
                    ? alias === 'news'
                      ? 'Bot zu aktuellen News befragen'
                      : alias === 'magazine'
                        ? 'Bot zu Inhalten des Magazins befragen'
                        : alias === 'forum'
                          ? 'Bot zu Inhalten des Forums befragen'
                          : alias === 'experts-actors'
                            ? 'Expertinnen und Experten auf Medtec Online per Bot suchen'
                            : 'Bot zu Communities und ihren Inhalten befragen'
                    : placeholder || 'Durchsuchen',
                onChange: e => {
                  const val = e.target.value;
                  let routeChanged = false;
                  if (botSearchActive && !!botAnswer && val?.length > 0) {
                    setBotResponseAction?.('', '');
                    dispatcher.resetNewsReferencesAction?.();
                  }
                  changeQueryTerm?.(val, !!botSearchActive);
                  if (!onSearchRoute && routeToSearch) {
                    let searchUrl = match.url + '/search';
                    if (alias === 'news' && val.length > 0) {
                      searchUrl += '?ref=/news';
                    } else if (alias === 'magazine' && val.length > 0) {
                      searchUrl += '?ref=/info/magazine';
                    } else if (alias === 'forum' && val.length > 0) {
                      searchUrl += '?ref=/info/forum';
                    } else if (alias === 'groups-projects' && val.length > 0) {
                      searchUrl += '?ref=/coop/groups-projects';
                    } else if (alias === 'experts-actors' && val.length > 0) {
                      searchUrl += '?ref=/info/experts-actors';
                    }
                    history.push(searchUrl);
                    routeChanged = true;
                  }
                  if ((!botSearchActive || val?.length === 0 || routeChanged) && val !== undefined && (val?.length > 1 || val?.length === 0 || (routeChanged && botSearchActive))) {
                    debouncedFetchFirstPageSearchEntries?.(botSearchActive && routeChanged);
                  }
                },
                onKeyPress: e => {
                  if (!!botSearchActive && e.key === 'Enter') {
                    setSearchWorkingAction?.(true);
                    if (alias === 'news' || alias === 'magazine' || alias === 'forum' || alias === 'groups-projects') {
                      debouncedBotFetchFirstPageSearchEntriesAction?.()
                    } else if (alias === 'experts-actors') {
                      debouncedFetchBotExpertAnswer?.();
                    }
                  }
                }
              }}
              inputLabel={{ htmlFor: 'head-search' }}
              clearOnClick={() => {
                const referrer = getReferrer(history.location.search);
                if (referrer) {
                  if (alias === 'news' || alias === 'magazine' || alias === 'forum' || alias === 'groups-projects') {
                    changeQueryTerm?.('');
                    if (botSearchActive && !!botAnswer) {
                      setBotResponseAction?.('', '');
                    }
                    if (!botSearchActive) {
                      resetSearchObject?.();
                    }
                    setSearchWorkingAction?.(true);
                    debouncedFetchFirstPageSearchEntries?.();
                    if (!botSearchActive) {
                      history.push(referrer);
                    }
                  } else {
                    history.push(referrer);
                  }
                } else {
                  changeQueryTerm?.('', !!botSearchActive);
                  if (botSearchActive && !!botAnswer) {
                    setBotResponseAction?.('', '');
                  }
                  debouncedFetchFirstPageSearchEntries?.();
                  if (!onSearchRoute && routeToSearch) {
                    history.push(match.url + '/search');
                  }
                }
              }}
            />
          </Bx>}
          {!searchInactive && <Bx>
            {!botSearchActive && <Btn
              size="large"
              startIcon={<FontAwesomeIcon icon={faSlidersV} />}
              onClick={() => {
                if (filterVisible) {
                  hideFilterAction?.();
                } else {
                  showFilterAction?.();
                }
              }}
              color={filterVisible ? 'primary' : 'default'}
              variant={filterVisible ? 'contained' : 'outlined'}
            >
              Filtern
            </Btn>}
            {(alias === 'news' || alias === 'magazine' || alias === 'forum' || alias === 'groups-projects' || alias === 'experts-actors') && botSearchActiveLocal && <>
              {botSearchActive && <IconBtn
                variant="contained" shape="circular"
                style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
                size="medium"
                onClick={() => {
                  setSearchWorkingAction?.(true);
                  setBotResponseAction?.('', '');
                  if (alias === 'experts-actors') {
                    debouncedFetchBotExpertAnswer?.();
                  } else {
                    debouncedFetchFirstPageSearchEntries?.();
                  }
                }}
                title='Abschicken'
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </IconBtn>}
              <Btn
                style={{ marginLeft: 8 }}
                size="large"
                startIcon={!!botSearchActive ? <FontAwesomeIcon icon={faSearch} /> : <FontAwesomeIcon icon={faMessageBot} />}
                onClick={() => {
                  if (!botSearchActive && !!botSearchActiveLocal) {
                    hideFilterAction?.();
                    resetSearchObject?.();
                    if (!onSearchRoute && routeToSearch) {
                      history.push(match.url + '/search');
                    }
                  }
                  setBotSearchAction?.();
                  changeQueryTerm?.('', !botSearchActive && !!botSearchActiveLocal);
                  if (botSearchActive && !!botAnswer) {
                    setBotResponseAction?.('', '');
                  }
                  dispatcher.resetNewsReferencesAction?.();
                  setSearchWorkingAction?.(true);
                  debouncedFetchFirstPageSearchEntries?.();
                }}
                color={'secondary'}
                variant={'contained'}
              >
                {!!botSearchActive
                  ? alias === 'news'
                    ? 'Nachrichten durchsuchen'
                    : alias === 'magazine'
                      ? 'Magazin durchsuchen'
                      : alias === 'forum'
                        ? 'Forum durchsuchen'
                        : alias === 'experts-actors'
                            ? 'Personen & Akteure suchen'
                            : 'Communities durchsuchen'
                  : alias === 'news'
                    ? 'Bot zu aktuellen News befragen'
                    : alias === 'magazine'
                      ? 'Bot zu Inhalten des Magazins befragen'
                      : alias === 'forum'
                        ? 'Bot zu Inhalten des Forums befragen'
                        : alias === 'experts-actors'
                            ? 'Experten finden per Bot'
                            : 'Bot zu Communities und ihren Inhalten befragen'
                  }
              </Btn>
            </>}
          </Bx>}
        </HeaderHeadbarContainer>
      </HeadBar>
      {!searchInactive && <HeaderFilter
        filterItem={filterItem}
        filterAlias={filterAlias}
        filtersVisible={filterVisible}
        filterWorking={filterWorking}
        filterSwitch={filterSwitch}
        searchObject={searchObject}
        searchTypes={searchTypes}
        selectedSearch={selectedSearch}
        searchResult={searchResult}
        handleClick={() => {}}
        setSelectedSearch={(a, b) => {
          setSelectedSearch?.(a, b);
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        resetSearchFilter={() => {
          resetSearchObject?.();
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onTypeChange={(thingType: string) => {
          changeQueryTypeAction?.(thingType);
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onDestinationChange={(destinationAlias: string) => {
          changeQueryDestinationAction?.(destinationAlias);
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onCategoryChange={(category: string, noToggle: boolean, parentCategoryId: string) => {
          changeQueryCategoriesAction?.(category, noToggle, parentCategoryId);
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onSortChange={(sortId: string) => {
          changeQuerySortAction?.(sortId);
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onSearchTypeChange={(searchTypeId: string) => {
          changeQuerySearchTypeAction?.(searchTypeId);
          fetchFirstPageSearchEntries?.();
        }}
        newsAllConcepts={newsAllConcepts}
        setNewsAllConcepts={setNewsAllConcepts}
      />}
    </>
  );
};

export const DashboardMobileHeader: React.FC<IDashboardHeaderProps & INavbarOpenProps> = ({
  alias,
  filterItem,
  filterAlias,
  routeToSearch,
  filterVisible,
  filterWorking,
  filterSwitch,
  placeholder,
  searchObject,
  searchTypes,
  selectedSearch,
  searchResult,
  changeQueryTerm,
  changeQueryTypeAction,
  changeQueryCategoriesAction,
  changeQueryDestinationAction,
  changeQuerySortAction,
  changeQuerySearchTypeAction,
  showFilterAction,
  hideFilterAction,
  fetchFirstPageSearchEntries,
  debouncedFetchBotExpertAnswer,
  debouncedFetchFirstPageSearchEntries,
  debouncedBotFetchFirstPageSearchEntriesAction,
  setSearchWorkingAction,
  resetSearchObject,
  setSelectedSearch,
  canCreateContent,
  createContentCaption,
  onCreateContentClicked,
  open,
  setOpen,
  memoryList,
  memoryIds,
  setNewsAllConcepts,
  newsAllConcepts,
  botSearchActive,
  setBotSearchAction,
  setBotResponseAction,
  botAnswer,
  botSearchInitial
}) => {
  const match = useRouteMatch();
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const pathSplit = history.location.pathname.split('/');
  const onSearchRoute = pathSplit[pathSplit.length - 1] === 'search';
  const onStartRoute = history.location.pathname === '/start';
  const [useSearch, setUseSearch] = React.useState(onSearchRoute);

  const initialSearchRef = useRef(onSearchRoute);
  const initiallyEmpty = useRef(false);

  useEffect(() => {
    if ((!memoryIds || memoryIds?.length === 0) && !initiallyEmpty.current) {
      initiallyEmpty.current = true;
    }
  });

  useEffect(() => {
    // Guard against firing the initial search twice (the header remounts when the mobile/desktop
    // breakpoint resolves, resetting initialSearchRef): skip if a search is already in flight or
    // has already produced results. This stops the duplicate concurrent search on landing.
    if (initialSearchRef.current && searchObject && filterItem && !searchResult) {
      initialSearchRef.current = false;
      fetchFirstPageSearchEntries?.();
    }
  }, [initialSearchRef, fetchFirstPageSearchEntries, searchObject, filterItem, searchResult]);

  const searchInactive = initiallyEmpty.current && !!memoryList && (!memoryIds || memoryIds?.length === 0);
  const botSearchActiveLocal = !!filterItem?.content?.botSearchActive;

  const dispatcher = {
    setQnaFlowInitAction: () => {
      dispatch(setFlowTypeAction({type: 'qna'}));
      dispatch(setFlowAnonymousAction({anonymous: false}));
    },
    // resetFlowAction: () => dispatch(resetFlowAction?.({})), // no workflow in mobile app
    resetNewsReferencesAction: () => dispatch(resetNewsReferences?.()),
  };

  return (
    <MobileHeadBar>
      <Bx display="flex" height="100%" minHeight="inherit" alignItems="center" py={1}>
        <Grid item xs={3}>
          <Bx display="flex" pr={1} alignItems="center" justifyContent="flex-start" height="100%">
            <IconBtn onClick={() => setOpen(open)}>
              <Hamburger width={30} height={30} size={20} toggled={open} label={open ? 'Menü schließen' : 'Menü öffnen'} />
            </IconBtn>
          </Bx>
        </Grid>
        <Grid item xs={6}>
          <Bx display="flex" alignItems="center" justifyContent="center" height="100%">
            <NavBarLogo to="/" src={MTOLogo.logoDarkSrc} />
          </Bx>
        </Grid>
        <Grid item xs={3}>
          <Bx display="flex" pl={1} alignItems="center" justifyContent="flex-end" height="100%">
            {<IconBtn
              onClick={() => {
                setUseSearch(!useSearch);
                if (filterVisible) hideFilterAction?.();
              }}
            >
              <FontAwesomeIcon icon={useSearch ? faTimes : faSearch} />
            </IconBtn>}
            {!onStartRoute && canCreateContent && (flowSteps[alias] || onCreateContentClicked) && (
              <Bx mx={1}>
                <IconBtn
                  size="small"
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    if (alias === 'forum') {
                      dispatcher.setQnaFlowInitAction?.();
                    }
                    onCreateContentClicked ? onCreateContentClicked() : history.push(match.url + '/flow');
                  }}
                  style={{ borderRadius: theme.shape.borderRadius }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </IconBtn>
              </Bx>
            )}
          </Bx>
        </Grid>
      </Bx>
      {!searchInactive && <Collapse in={useSearch}>
        <>
          <Bx display="flex" height="100%" minHeight={64} alignItems="center" py={1}>
            <Bx display="flex" width="100%" zIndex={2}>
              <Bx pl={1} flexGrow={1} flexShrink={1} flexBasis="auto">
                <HeadSearchInput
                  searchIcon={null}
                  // searchIcon={!!botSearchActive && <FontAwesomeIcon icon={faMessageBot} />}
                  input={{
                    id: 'head-search',
                    value: searchObject?.queryTerm,
                    placeholder: alias === 'start'
                      ? filterItem?.content?.title || 'Medtec Online durchsuchen'
                      : !!botSearchActive
                        ? alias === 'news'
                          ? 'Bot zu aktuellen News befragen'
                          : alias === 'magazine'
                            ? 'Bot zu Inhalten des Magazins befragen'
                            : alias === 'forum'
                              ? 'Bot zu Inhalten des Forums befragen'
                              : alias === 'experts-actors'
                                ? 'Expertinnen und Experten auf Medtec Online per Bot suchen'
                                : 'Bot zu Communities und ihren Inhalten befragen'
                        : placeholder || 'Durchsuchen',
                    onChange: e => {
                      const val = e.target.value;
                      let routeChanged = false;
                      if (botSearchActive && !!botAnswer && val?.length > 0) {
                        setBotResponseAction?.('', '');
                        dispatcher.resetNewsReferencesAction?.();
                      }
                      changeQueryTerm?.(val, !!botSearchActive);
                      if (!onSearchRoute && routeToSearch) {
                        let searchUrl = match.url + '/search';
                        if (alias === 'news' && val.length > 0) {
                          searchUrl += '?ref=/news';
                        } else if (alias === 'magazine' && val.length > 0) {
                          searchUrl += '?ref=/info/magazine';
                        } else if (alias === 'forum' && val.length > 0) {
                          searchUrl += '?ref=/info/forum';
                        } else if (alias === 'groups-projects' && val.length > 0) {
                          searchUrl += '?ref=/coop/groups-projects';
                        } else if (alias === 'experts-actors' && val.length > 0) {
                          searchUrl += '?ref=/info/experts-actors';
                        }
                        history.push(searchUrl);
                        routeChanged = true;
                      }
                      if ((!botSearchActive || val?.length === 0 || routeChanged) && val !== undefined && (val?.length > 1 || val?.length === 0 || (routeChanged && botSearchActive))) {
                        debouncedFetchFirstPageSearchEntries?.(botSearchActive && routeChanged);
                      }
                    },
                    onKeyPress: e => {
                      if (!!botSearchActive && e.key === 'Enter') {
                        setSearchWorkingAction?.(true);
                        if (alias === 'news' || alias === 'magazine' || alias === 'forum' || alias === 'groups-projects') {
                          debouncedBotFetchFirstPageSearchEntriesAction?.()
                        } else if (alias === 'experts-actors') {
                          debouncedFetchBotExpertAnswer?.();
                        }
                      }
                    }
                  }}
                  inputLabel={{ htmlFor: 'head-search' }}
                  clearOnClick={() => {
                    const referrer = getReferrer(history.location.search);
                    if (referrer) {
                      if (alias === 'news' || alias === 'magazine' || alias === 'forum' || alias === 'groups-projects') {
                        changeQueryTerm?.('');
                        if (botSearchActive && !!botAnswer) {
                          setBotResponseAction?.('', '');
                        }
                        if (!botSearchActive) {
                          resetSearchObject?.();
                        }
                        setSearchWorkingAction?.(true);
                        debouncedFetchFirstPageSearchEntries?.();
                        if (!botSearchActive) {
                          history.push(referrer);
                        }
                      } else {
                        history.push(referrer);
                      }
                    } else {
                      changeQueryTerm?.('', !!botSearchActive);
                      if (botSearchActive && !!botAnswer) {
                        setBotResponseAction?.('', '');
                      }
                      debouncedFetchFirstPageSearchEntries?.();
                      if (!onSearchRoute && routeToSearch) {
                        history.push(match.url + '/search');
                      }
                    }
                  }}
                />
              </Bx>
              <Bx display={'flex'}>
                {!botSearchActive && <IconBtn
                  size="small"
                  color={filterVisible ? 'primary' : 'default'}
                  variant={filterVisible ? 'contained' : 'outlined'}
                  onClick={() => {
                    if (filterVisible) {
                      hideFilterAction?.();
                    } else {
                      showFilterAction?.();
                    }
                  }}
                  shape="rounded"
                  title="Filtern"
                >
                  <FontAwesomeIcon icon={faSlidersV} />
                </IconBtn>}
                {(alias === 'news' || alias === 'magazine' || alias === 'forum' || alias === 'groups-projects' || alias === 'experts-actors') && botSearchActiveLocal && <>
                  {botSearchActive && <IconBtn
                    variant="contained" shape="circular"
                    style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
                    size="small"
                    onClick={() => {
                      setSearchWorkingAction?.(true);
                      setBotResponseAction?.('', '');
                      if (alias === 'experts-actors') {
                        debouncedFetchBotExpertAnswer?.();
                      } else {
                        debouncedFetchFirstPageSearchEntries?.();
                      }
                    }}
                    title='Abschicken'
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </IconBtn>}
                  <IconBtn
                    style={{ marginLeft: 8 }}
                    size="small"
                    onClick={() => {
                      if (!botSearchActive && !!botSearchActiveLocal) {
                        hideFilterAction?.();
                        resetSearchObject?.();
                        if (!onSearchRoute && routeToSearch) {
                          history.push(match.url + '/search');
                        }
                      }
                      setBotSearchAction?.();
                      changeQueryTerm?.('', !botSearchActive && !!botSearchActiveLocal);
                      if (botSearchActive && !!botAnswer) {
                        setBotResponseAction?.('', '');
                      }
                      dispatcher.resetNewsReferencesAction?.();
                      setSearchWorkingAction?.(true);
                      debouncedFetchFirstPageSearchEntries?.();
                    }}
                    color={'secondary'}
                    variant={'contained'}
                    shape="rounded"
                    title={!!botSearchActive
                      ? alias === 'news'
                        ? 'Nachrichten durchsuchen'
                        : alias === 'magazine'
                          ? 'Magazin durchsuchen'
                          : alias === 'forum'
                            ? 'Forum durchsuchen'
                            : alias === 'experts-actors'
                              ? 'Personen & Akteure suchen'
                              : 'Communities durchsuchen'
                      : alias === 'news'
                        ? 'Bot zu aktuellen News befragen'
                        : alias === 'magazine'
                          ? 'Bot zu Inhalten des Magazins befragen'
                          : alias === 'forum'
                            ? 'Bot zu Inhalten des Forums befragen'
                            : alias === 'experts-actors'
                              ? 'Experten finden per Bot'
                              : 'Bot zu Communities und ihren Inhalten befragen'}
                  >
                    {!!botSearchActive ? <FontAwesomeIcon icon={faSearch} /> : <FontAwesomeIcon icon={faMessageBot} />}
                  </IconBtn>
                </>}
              </Bx>
            </Bx>
          </Bx>
        </>
      </Collapse>}
      {!searchInactive && <HeaderFilter
        smallVariant={true}
        filterItem={filterItem}
        filterAlias={filterAlias}
        filtersVisible={filterVisible}
        filterWorking={filterWorking}
        filterSwitch={filterSwitch}
        searchObject={searchObject}
        searchTypes={searchTypes}
        selectedSearch={selectedSearch}
        searchResult={searchResult}
        handleClick={() => {}}
        setSelectedSearch={(a, b) => {
          setSelectedSearch?.(a, b);
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        resetSearchFilter={() => {
          resetSearchObject?.();
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onTypeChange={(thingType: string) => {
          changeQueryTypeAction?.(thingType);
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onDestinationChange={(destinationAlias: string) => {
          changeQueryDestinationAction?.(destinationAlias);
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onCategoryChange={(category: string, noToggle: boolean, parentCategoryId: string) => {
          changeQueryCategoriesAction?.(category, noToggle, parentCategoryId);
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onSortChange={(sortId: string) => {
          changeQuerySortAction?.(sortId);
          fetchFirstPageSearchEntries?.();
          if (!onSearchRoute && routeToSearch) {
            history.push(match.url + '/search');
          }
        }}
        onSearchTypeChange={(searchTypeId: string) => {
          changeQuerySearchTypeAction?.(searchTypeId);
          fetchFirstPageSearchEntries?.();
        }}
        newsAllConcepts={newsAllConcepts}
        setNewsAllConcepts={setNewsAllConcepts}
      />}
    </MobileHeadBar>
  );
};

export interface IConnectedDashboardHeader {
  alias: string;
  placeholder?: string;
  onCreateContentClicked?: () => void;
  routeToSearch?: boolean;
}

export const ConnectedDashboardHeader: React.FC<IConnectedDashboardHeader> = ({ alias, placeholder, onCreateContentClicked, routeToSearch }) => {
  const dispatch = useDispatch();
  const flowSearch = useSelector(state => state.filter?.flowSearch);
  if (flowSearch) {
    dispatch(fetchFilterRequestAction({ alias }));
    dispatch(setSelectedSearchAction({ searchAlias: alias }));
  }
  const isMobile = useIsMobile();
  const navBarOpen = useSelector(state => state.foundation?.navbarOpen);
  const result = useSelector(state => state.search?.result);
  const config = useSelector(state => state.foundation?.appconfig?.[alias]);
  const memoryList = useSelector(state => state.foundation?.profile?.memoryList);
  const communications = useSelector(state => state.communication?.communications);
  const communicationsWorking = useSelector(state => state.communication?.communicationsWorking);
  const searchTypes = useSelector(state => state.filter?.filterItem?.content?.searchTypes);
  const selectedSearch = useSelector(state => state.filter?.selectedSearch);
  const filterSwitch = useSelector(state => state.filter?.filterItem?.content?.filterSwitch);
  const setNavbarOpen = () => dispatch(setNavbarOpenAction(!navBarOpen));
  const newsAllConcepts = useSelector(state => state.news?.allConcepts);
  const botSearchActive = useSelector(state => state.bot?.botSearchActive);
  const botAnswer = useSelector(state => state.bot?.botSearch?.botAnswer);

  const [dispatcher, setDispatcher] = useState({} as any);
  const { searchObject, filterVisible, filterItem, filterAlias, filterWorking } = useFilterState();

  useEffect(() => {
    if (alias === 'news') {
      setDispatcher(() => ({
        changeQueryTermAction: (term: string) => {
          dispatch(changeQueryTermAction?.({ term }));
        },
        changeQueryTypeAction: () => {},
        changeQueryDestinationAction: () => {},
        changeQueryCategoriesAction: (category: string, noToggle: boolean, parentCategoryId: string) =>
          dispatch(changeQueryCategoriesAction?.({ category, noToggle, parentCategoryId })),
        changeQuerySortAction: (sortId: string) => dispatch(changeQuerySortAction?.({ sortId })),
        changeQuerySearchTypeAction: (searchTypeId: 'combined' | 'combined2' | 'ontology' | 'elastic') => dispatch(changeQuerySearchTypeAction?.({ searchTypeId })),
        resetSearchObjectAction: () => {
          dispatch(resetSearchObjectAction?.({ keepFilter: true, keepFilterAlias: true }));
        },
        showFilterAction: () => dispatch(showFilterAction?.({})),
        hideFilterAction: () => dispatch(hideFilterAction?.({})),
        fetchFirstPageSearchEntriesAction: () => {
          dispatch(fetchFirstPageNewsRequestAction?.({}));
        },
        debouncedFetchFirstPageSearchEntriesAction: _debounce((defaultResultsOnly?: boolean) => {
          dispatch(fetchFirstPageNewsRequestAction?.({defaultResultsOnly}));
        }, 350),
        debouncedBotFetchFirstPageSearchEntriesAction: _debounce(() => {
          dispatch(fetchFirstPageNewsRequestAction?.({}));
        }, 1000),
        setSelectedSearchAction: (searchAlias: string, keepQuery: boolean) => { // load different search by alias
          if (searchAlias) {
            dispatch(setBotSearchAction({ botSearchActive: false }));
            dispatch(resetSearchObjectAction({ keepFilter: true, keepQuery }));
            dispatch(clearSearchEntriesAction({}));
            dispatch(fetchFilterRequestAction({ alias: searchAlias }));
            dispatch(setSelectedSearchAction({ searchAlias }));
            dispatch(fetchFirstPageNewsRequestAction?.({}));
          }
        },
        setNewsAllConcepts: () => {
          dispatch(setNewsAllConcepts?.());
          dispatch(fetchFirstPageNewsRequestAction?.({}));
        },
        setSearchWorking: (working: boolean) => dispatch(setNewsWorking({ working })),
        setBotSearchAction: () => dispatch(setBotSearchAction({})),
        setBotResponseAction: (botAnswer: string, botRequestId: string) => dispatch(botResponseEndAction?.({response:botAnswer, request_id: botRequestId})),
      }));
    } else {
      setDispatcher(() => ({
        changeQueryTermAction: (term: string, noFilterAction?: boolean) => {
          dispatch(changeQueryTermAction?.({ term }));
          if (!noFilterAction && !!term) {
            dispatch(showFilterAction?.({}));
          }
        },
        changeQueryTypeAction: (thingType: string) => dispatch(changeQueryTypeAction?.({ thingType })),
        changeQueryDestinationAction: (destinationAlias: string) => dispatch(changeQueryDestinationAction?.({ destinationAlias })),
        changeQueryCategoriesAction: (category: string, noToggle: boolean, parentCategoryId: string) =>
          dispatch(changeQueryCategoriesAction?.({ category, noToggle, parentCategoryId })),
        changeQuerySortAction: (sortId: string) => dispatch(changeQuerySortAction?.({ sortId })),
        resetSearchObjectAction: () => dispatch(resetSearchObjectAction?.({ keepFilter: true, keepFilterAlias: true })),
        showFilterAction: () => dispatch(showFilterAction?.({})),
        hideFilterAction: () => dispatch(hideFilterAction?.({})),
        fetchFirstPageSearchEntriesAction: () => {
          dispatch(fetchFirstPageSearchEntriesRequestAction?.({}));
        },
        debouncedFetchBotExpertAnswerRequest: _debounce(() => {
          dispatch(fetchBotExpertAnswerRequestAction?.({}));
        }, 350),
        debouncedFetchFirstPageSearchEntriesAction: _debounce((defaultResultsOnly?: boolean) => {
          dispatch(fetchFirstPageSearchEntriesRequestAction?.({defaultResultsOnly}));
        }, 350),
        debouncedBotFetchFirstPageSearchEntriesAction: _debounce(() => {
          dispatch(fetchFirstPageSearchEntriesRequestAction?.({}));
        }, 1000),
        setSelectedSearchAction: (searchAlias: string, keepQuery: boolean) => {
          if (searchAlias) {
            dispatch(setBotSearchAction({ botSearchActive: false }));
            dispatch(resetSearchObjectAction({ keepFilter: true, keepQuery }));
            dispatch(clearSearchEntriesAction({}));
            dispatch(fetchFilterRequestAction({ alias: searchAlias }));
            dispatch(setSelectedSearchAction({ searchAlias }));
            dispatch(fetchFirstPageSearchEntriesRequestAction?.({}));
          }
        },
        setSearchWorking: (working: boolean) => dispatch(setSearchWorkingAction({ working })),
        setBotSearchAction: () => dispatch(setBotSearchAction({})),
        setBotResponseAction: (botAnswer: string, botRequestId: string) => dispatch(botResponseEndAction?.({response:botAnswer, request_id: botRequestId})),
      }));
    }
  }, [dispatch, alias]);

  let canCreateContent = config?.canCreateContent;
  if (config?.showSidebar && config.sidebarCreate && (communications?.length || communicationsWorking)) {
    canCreateContent = false;
  }

  const DashboardHeaderProps = {
    routeToSearch: routeToSearch === false ? false : true,
    alias: alias,
    canCreateContent: canCreateContent,
    createContentCaption: config?.createContentCaption,
    placeholder: placeholder,
    filterItem: filterItem,
    filterAlias: filterAlias,
    filterVisible: filterVisible,
    filterWorking: filterWorking,
    filterSwitch: filterSwitch,
    searchObject: searchObject,
    searchTypes: searchTypes,
    selectedSearch: selectedSearch,
    searchResult: result,
    changeQueryTerm: dispatcher.changeQueryTermAction,
    changeQueryTypeAction: dispatcher.changeQueryTypeAction,
    changeQueryDestinationAction: dispatcher.changeQueryDestinationAction,
    changeQueryCategoriesAction: dispatcher.changeQueryCategoriesAction,
    changeQuerySortAction: dispatcher.changeQuerySortAction,
    changeQuerySearchTypeAction: dispatcher.changeQuerySearchTypeAction,
    fetchFirstPageSearchEntries: dispatcher.fetchFirstPageSearchEntriesAction,
    debouncedFetchBotExpertAnswer: dispatcher.debouncedFetchBotExpertAnswerRequest,
    debouncedFetchFirstPageSearchEntries: dispatcher.debouncedFetchFirstPageSearchEntriesAction,
    debouncedBotFetchFirstPageSearchEntriesAction: dispatcher.debouncedBotFetchFirstPageSearchEntriesAction,
    resetSearchObject: dispatcher.resetSearchObjectAction,
    showFilterAction: dispatcher.showFilterAction,
    hideFilterAction: dispatcher.hideFilterAction,
    setSelectedSearch: dispatcher.setSelectedSearchAction,
    setNewsAllConcepts: dispatcher.setNewsAllConcepts,
    setSearchWorkingAction: dispatcher.setSearchWorking,
    memoryList: config?.dashboardMemoryLists,
    memoryIds: memoryList?.map(m => m.elementId),
    onCreateContentClicked,
    newsAllConcepts: newsAllConcepts,
    botSearchActive: botSearchActive,
    setBotSearchAction: dispatcher.setBotSearchAction,
    setBotResponseAction: dispatcher.setBotResponseAction,
    botAnswer: botAnswer,
    botSearchInitial: config?.botSearchInitial
  };

  if (isMobile) return <DashboardMobileHeader open={!!navBarOpen} setOpen={setNavbarOpen} {...DashboardHeaderProps} />;
  else return <DashboardHeader {...DashboardHeaderProps} />;
};
